import normalizeError from '../utils/normalizeError';
import { clearTokens } from '../utils/tokens';
import instance from './scorpiusInstance';
import {
  LOGOUT,
  SCORPIUS_LOGOUT_START,
  SCORPIUS_LOGOUT_SUCCESS,
  SCORPIUS_LOGOUT_ERROR,
} from './types';

/**
 * Logs the user out by clearing localStorage and resetting redux state,
 * see reducers/index.js.
 * NOTE: This remains local-only for backward compatibility. Use
 * `scorpiusLogout` when server-side refresh-token revocation is required.
 */
export const logout = () => dispatch => {
  dispatch({ type: LOGOUT });
  clearTokens();
  return Promise.resolve();
};

// Marks the beginning of the revoke-capable logout flow so callers/UI can
// treat logout as in-progress before the /user/logout request resolves.
export const scorpiusLogoutStart = () => ({
  type: SCORPIUS_LOGOUT_START,
});

// Signals that server-side revoke completed; local cleanup still happens in
// finally to guarantee browser-session logout semantics.
export const scorpiusLogoutSuccess = () => ({
  type: SCORPIUS_LOGOUT_SUCCESS,
});

// Signals revoke failure for callers that want telemetry/UX, while local
// browser-session cleanup still proceeds in finally.
export const scorpiusLogoutError = error => ({
  type: SCORPIUS_LOGOUT_ERROR,
  error,
});

/**
 * Logs the user out by invalidating their refresh token and removes the tokens
 * from localStorage.
 *
 * IMPORTANT: Revocation is at the user-account level, not the session level.
 * This invalidates all existing refresh tokens for that user across all active
 * sessions/devices for the account. Existing access tokens remain valid until
 * they expire.
 */
export const scorpiusLogout = () => async dispatch => {
  dispatch(scorpiusLogoutStart());
  try {
    // Business rule: attempt server-side revoke first so shared-account refresh
    // tokens are invalidated at the user-account level before local cleanup.
    // Use retry-aware request config so the interceptor can refresh
    // an expired access token during logout and still revoke refresh tokens.
    await instance.post('/user/logout', null, { headers: { retry: true } });
    return dispatch(scorpiusLogoutSuccess());
  } catch (error) {
    // Preserve existing error semantics for callers that need failure handling.
    throw dispatch(scorpiusLogoutError(normalizeError(error)));
  } finally {
    // Business rule: never block logout UX on revoke failures.
    // Local auth state is always cleared so the current browser session ends.
    dispatch(logout());
  }
};

export default logout;
